// src/js/core/store.js
//
// Proxyベースの軽量ストア。フレームワークを使わない方針（IMPLEMENTATION.md 5.1）のため、
// 状態の変更検知は素の Proxy の get/set トラップで行い、通知は queueMicrotask で
// 同一tick内の複数変更を1回にバッチ化する。
//
// 00_情報設計_全体アーキテクチャ.md 5.1節の状態一覧（再生状態、音声ON/OFF、グリッド密度、
// 並べ替え、シャッフルseed、既読、いいね、ブックマーク、フィード位置）を単一のstateツリーで
// 扱えるようにし、永続化先（メモリ/localStorage/カスタム=IndexedDB等）をキーごとに
// 宣言的に切り替えられるようにする。store.js自体は core/db.js に依存しない
// （永続化はcreateDbBackedRecord等のアダプタを呼び出し側が注入する形にし、レイヤーを分離する）。

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
}

function getAtPath(obj, path) {
  if (!path) return obj;
  let cur = obj;
  for (const seg of path.split('.')) {
    if (cur === null || cur === undefined) return undefined;
    cur = cur[seg];
  }
  return cur;
}

/** path の親までを辿り、最後のセグメントへの代入を呼び出し側に行わせる（Proxyのsetトラップを発火させるため）。 */
function setAtPath(rootProxy, path, value) {
  const segments = path.split('.');
  let cur = rootProxy;
  for (let i = 0; i < segments.length - 1; i += 1) {
    const seg = segments[i];
    if (!isPlainObject(cur[seg])) {
      cur[seg] = {};
    }
    cur = cur[seg];
  }
  cur[segments[segments.length - 1]] = value;
}

function structuredCloneSafe(value) {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch {
      // 関数・非構造化可能な値を含む場合はJSONベースのフォールバックに回す。
    }
  }
  return JSON.parse(JSON.stringify(value));
}

function readLocalStorage(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function writeLocalStorage(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 容量超過・プライベートブラウジング等。永続化は諦めるがストア自体は継続動作する
    // （db.js と同じ「壊れないこと」の方針）。
  }
}

/**
 * オブジェクトツリーを再帰的にProxy化する。ネストしたオブジェクトへの直接代入
 * （例: store.get('feed').position = 3 のような取得後の書き換え）でも
 * 変更通知・永続化が正しく発火するようにするための実装。
 */
function wrapTree(target, pathPrefix, onChange) {
  if (!isPlainObject(target)) return target;

  return new Proxy(target, {
    get(obj, prop, receiver) {
      const value = Reflect.get(obj, prop, receiver);
      if (typeof prop === 'symbol') return value;
      if (isPlainObject(value)) {
        const childPath = pathPrefix ? `${pathPrefix}.${prop}` : prop;
        return wrapTree(value, childPath, onChange);
      }
      return value;
    },
    set(obj, prop, value) {
      if (typeof prop === 'symbol') {
        obj[prop] = value;
        return true;
      }
      obj[prop] = value;
      const path = pathPrefix ? `${pathPrefix}.${prop}` : String(prop);
      onChange(path);
      return true;
    },
    deleteProperty(obj, prop) {
      const existed = prop in obj;
      delete obj[prop];
      if (existed && typeof prop !== 'symbol') {
        const path = pathPrefix ? `${pathPrefix}.${prop}` : String(prop);
        onChange(path);
      }
      return true;
    },
  });
}

/**
 * @typedef {{ type: 'memory' }} MemoryPersistence
 * @typedef {{ type: 'localStorage', key?: string }} LocalStoragePersistence
 * @typedef {{ type: 'custom', read(): any, write(value: any): void }} CustomPersistence
 * @typedef {MemoryPersistence|LocalStoragePersistence|CustomPersistence} PersistenceDescriptor
 */

/**
 * @param {object} initialState 初期状態ツリー
 * @param {object} [options]
 * @param {Record<string, PersistenceDescriptor>} [options.persistence]
 *   状態パス（dot記法。例: 'ui.soundMuted'）ごとの永続化先。未指定のパスは 'memory'。
 *   'custom' の read/write は同期関数として呼ばれる想定（core/db.js の
 *   createDbBackedRecord() のように、非同期I/Oはあらかじめメモリキャッシュへ
 *   読み込んでおいたうえで同期の read/write を提供するアダプタにすること）。
 * @returns {{
 *   get(path?: string): any,
 *   set(path: string, value: any): void,
 *   subscribe(pathOrCallback: string|null|((state: any, changedPaths: Set<string>) => void), callback?: (value: any, changedPaths: Set<string>) => void): () => void,
 *   batch(fn: () => void): void,
 * }}
 */
export function createStore(initialState = {}, options = {}) {
  const persistenceMap = options.persistence || {};
  const listeners = new Set(); // { path: string|null, cb: function }

  let pendingPaths = new Set();
  let notifyScheduled = false;
  let batchDepth = 0;

  const rawState = structuredCloneSafe(initialState);

  function findPersistence(path) {
    if (persistenceMap[path]) return { path, desc: persistenceMap[path] };
    const segments = path.split('.');
    for (let i = segments.length - 1; i > 0; i -= 1) {
      const ancestor = segments.slice(0, i).join('.');
      if (persistenceMap[ancestor]) return { path: ancestor, desc: persistenceMap[ancestor] };
    }
    return null;
  }

  function writePersisted(resolvedPath, desc) {
    if (!desc || desc.type === 'memory') return;
    const value = getAtPath(rawState, resolvedPath);
    if (desc.type === 'localStorage') {
      writeLocalStorage(desc.key || `xte:${resolvedPath}`, value);
      return;
    }
    if (desc.type === 'custom' && typeof desc.write === 'function') {
      try {
        desc.write(value);
      } catch (err) {
        console.warn(`[store] 永続化の書き込みに失敗しました (${resolvedPath}):`, err);
      }
    }
  }

  function readPersisted(path, desc) {
    if (!desc || desc.type === 'memory') return undefined;
    if (desc.type === 'localStorage') return readLocalStorage(desc.key || `xte:${path}`);
    if (desc.type === 'custom' && typeof desc.read === 'function') {
      try {
        return desc.read();
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  // 起動時に永続化先から復元する（localStorage/カスタムアダプタ優先。初期状態は既定値扱い）。
  for (const [path, desc] of Object.entries(persistenceMap)) {
    const persisted = readPersisted(path, desc);
    if (persisted !== undefined) {
      const segments = path.split('.');
      let cur = rawState;
      for (let i = 0; i < segments.length - 1; i += 1) {
        const seg = segments[i];
        if (!isPlainObject(cur[seg])) cur[seg] = {};
        cur = cur[seg];
      }
      cur[segments[segments.length - 1]] = persisted;
    }
  }

  function flush() {
    notifyScheduled = false;
    const changedPaths = pendingPaths;
    pendingPaths = new Set();

    for (const listener of listeners) {
      if (listener.path === null) {
        listener.cb(proxiedState, changedPaths);
        continue;
      }
      for (const changed of changedPaths) {
        if (
          changed === listener.path ||
          changed.startsWith(`${listener.path}.`) ||
          listener.path.startsWith(`${changed}.`)
        ) {
          listener.cb(getAtPath(proxiedState, listener.path), changedPaths);
          break;
        }
      }
    }
  }

  function onChange(path) {
    const found = findPersistence(path);
    if (found) writePersisted(found.path, found.desc);

    pendingPaths.add(path);
    if (batchDepth > 0) return;
    if (notifyScheduled) return;
    notifyScheduled = true;
    queueMicrotask(flush);
  }

  const proxiedState = wrapTree(rawState, '', onChange);

  return {
    get(path) {
      return getAtPath(proxiedState, path);
    },

    set(path, value) {
      if (!path) throw new Error('[store] set() には path を指定してください。');
      setAtPath(proxiedState, path, value);
    },

    /**
     * 複数の set() を1つの通知にまとめる。queueMicrotask による自動バッチ化は
     * 同一tick内であれば既に効くが、明示的にバッチ境界を示したい呼び出し側
     * （例: フィード位置とスクロールYを同時に更新する）のために用意する。
     */
    batch(fn) {
      batchDepth += 1;
      try {
        fn();
      } finally {
        batchDepth -= 1;
        if (batchDepth === 0 && pendingPaths.size > 0 && !notifyScheduled) {
          notifyScheduled = true;
          queueMicrotask(flush);
        }
      }
    },

    /**
     * @param {string|null|((state:any, changedPaths:Set<string>)=>void)} pathOrCallback
     *   null または省略で state 全体の変更を購読する。
     * @param {(value:any, changedPaths:Set<string>)=>void} [callback]
     * @returns {() => void} 購読解除関数
     */
    subscribe(pathOrCallback, callback) {
      const path = typeof pathOrCallback === 'function' ? null : pathOrCallback ?? null;
      const cb = typeof pathOrCallback === 'function' ? pathOrCallback : callback;
      if (typeof cb !== 'function') {
        throw new Error('[store] subscribe() にはコールバック関数が必要です。');
      }
      const entry = { path, cb };
      listeners.add(entry);
      return () => listeners.delete(entry);
    },
  };
}
